import type { Result, ResultAsync } from "neverthrow";
import {
  ResultAsync as Async,
  err,
  ok,
  Result as ResultStatic,
} from "neverthrow";
import z from "zod";

import {
  buildInvalidError,
  getErrorMessage,
  mustParseAny,
  type InvalidError,
} from "@thalamos/common";

import {
  digestOf,
  InstrumentDigestSchema,
  type DigestError,
  type InstrumentDigest,
} from "./digest.js";
import type { InstrumentModule, ManifestEntry } from "./instrument.js";
import { UNPINNED } from "./instrument.js";
import {
  familyOf,
  InstrumentNameSchema,
  renderInstrumentName,
} from "./name.js";
import {
  itemsOf,
  parsePublishedInstrumentSchema,
  type PublishedInstrumentSchema,
} from "./publishedSchema.js";
import {
  commentKeyIssues,
  identifierSpellingIssues,
  nameSpellingIssues,
} from "./spelling.js";

/**
 * Where a published instrument answers from.
 *
 * Part of what is digested, so it is a constant rather than a setting: a name
 * that moved with the environment would give one instrument a different
 * identity in each, and an assessment started in one could not be validated in
 * another.
 */
const ID_BASE = "https://haven.thalamos.co.uk/instruments";

/** What a caller fetches a published instrument by, and what its `$id` says. */
export const instrumentId = (module: InstrumentModule): string =>
  `${ID_BASE}/${renderInstrumentName(module.name)}`;

const issuesOf = (message: string, issues: string[]) =>
  buildInvalidError({ message, issues });

const asJsonSchema = ResultStatic.fromThrowable(
  (schema: InstrumentModule["schema"]) =>
    z.toJSONSchema(schema, { target: "draft-2020-12" }),
  (cause) => issuesOf("A module has no JSON Schema", [getErrorMessage(cause)]),
);

/**
 * The published form of a module: JSON Schema, draft 2020-12, named by `$id`.
 *
 * Refused where the result falls outside the subset an instrument may be
 * published in — a group that is not a strict object, an item that is not one
 * response, a comment that is not about an item beside it — or whose name, or
 * an identifier it declares, is not spelt as one, or whose comment is not keyed
 * by its item. Catching it here is what lets every later reader treat a
 * published schema as having that shape.
 *
 * The spelling is held here rather than by `parsePublishedInstrumentSchema`,
 * which also reads stored rows: a rule about what may be written must not stop
 * anything already written from being read.
 */
export const publishedSchemaOf = (
  module: InstrumentModule,
): Result<PublishedInstrumentSchema, InvalidError> =>
  // `z.toJSONSchema` throws for a schema JSON Schema has no spelling for — a
  // date, a transform — so a module carrying one is a publication failure to
  // report beside the others rather than an exception out of a `Result`.
  asJsonSchema(module.schema)
    .andThen((document) =>
      parsePublishedInstrumentSchema({
        ...document,
        $id: instrumentId(module),
      }),
    )
    .andThen((schema) => {
      const issues = [
        ...nameSpellingIssues(module.name),
        ...identifierSpellingIssues(schema),
        ...commentKeyIssues(schema),
      ];

      return issues.length === 0
        ? ok(schema)
        : err(
            issuesOf(
              "An instrument's identifiers are not spelt as its rules require",
              issues,
            ),
          );
    });

/** The name a published schema is stored and pinned under. */
export const digestOfModule = (
  module: InstrumentModule,
): ResultAsync<InstrumentDigest, DigestError> =>
  publishedSchemaOf(module)
    .asyncAndThen(digestOf)
    .andThen(mustParseAny(InstrumentDigestSchema, "Not an instrument digest"));

/**
 * What is wrong with a module's keys, if anything.
 *
 * The keys exist so a collector can name one with the compiler checking it, and
 * that is only worth anything if they are the schema's keys. A key exported and
 * not asked would type-check at the collector and fail at the document.
 *
 * A comment is not exported: a collector names the item it is about, and the
 * comment's key follows from that.
 *
 * Every issue names the version it is about, so a manifest reporting several
 * says which is which.
 */
export const keysMatchSchemaIssues = (
  module: InstrumentModule,
  schema: PublishedInstrumentSchema,
): string[] => {
  const name = renderInstrumentName(module.name);
  const issues: string[] = [];

  const asked = itemsOf(schema);
  const askedItems = new Set(
    asked
      .filter((entry) => entry.commentOn === undefined)
      .map((entry) => entry.item),
  );
  const askedGroups = new Set(asked.map((entry) => entry.group));
  const exportedItems = new Set(Object.values(module.items));
  const exportedGroups = new Set(Object.values(module.groups));

  const report = (
    exported: Set<string>,
    present: Set<string>,
    kind: string,
  ) => {
    for (const key of exported) {
      if (!present.has(key)) {
        issues.push(
          `${name} exports ${kind} "${key}", which its schema does not ask`,
        );
      }
    }

    for (const key of present) {
      if (!exported.has(key)) {
        issues.push(`${name} asks ${kind} "${key}", which it does not export`);
      }
    }
  };

  report(exportedGroups, askedGroups, "group");
  report(exportedItems, askedItems, "item");

  // A key is already an identifier, so an export naming it any other way is a
  // second spelling for a reader to translate between.
  const misnamed = (exports: Readonly<Record<string, string>>, kind: string) =>
    Object.entries(exports)
      .filter(([exportName, key]) => exportName !== key)
      .map(
        ([exportName, key]) =>
          `${name} exports ${kind} "${key}" as ${exportName}; name it by its key`,
      );

  issues.push(
    ...misnamed(module.groups, "group"),
    ...misnamed(module.items, "item"),
  );

  // A key is the item's identity, so one asked in two groups names two
  // questions at once.
  const askedTwice = new Set(
    asked
      .map((entry) => entry.item)
      .filter((item, index, all) => all.indexOf(item) !== index),
  );

  for (const item of askedTwice) {
    issues.push(
      `${name} asks item "${item}" in more than one group — a key names one question`,
    );
  }

  for (const retired of module.retiredItems) {
    if (askedItems.has(retired)) {
      issues.push(
        `${name} asks "${retired}", which it also lists as retired — a retired key is dead for the life of the product`,
      );
    }
  }

  return issues;
};

/** Whether a module's exported keys say what its schema asks. */
export const checkKeysMatchSchema = (
  module: InstrumentModule,
  schema: PublishedInstrumentSchema,
): Result<void, InvalidError> => {
  const issues = keysMatchSchemaIssues(module, schema);

  return issues.length === 0
    ? ok(undefined)
    : err(issuesOf("A module does not export the keys it asks", issues));
};

/**
 * The retired list a version must carry, given the one it followed: everything
 * the predecessor had retired, plus every key the predecessor asked and this
 * version does not.
 *
 * Derived here and written in the module, and the two are compared. The written
 * list is the statement a reviewer reads in a diff; this is what stops it
 * drifting from the schema it claims to describe.
 *
 * A comment is never retired: it can only ever mean the comment on its item, so
 * it lives and dies with that item, and dropping one alone leaves its key free
 * to be declared again.
 */
export const derivedRetiredItems = (
  predecessor: { module: InstrumentModule; schema: PublishedInstrumentSchema },
  schema: PublishedInstrumentSchema,
): string[] => {
  const answersIn = (published: PublishedInstrumentSchema) =>
    itemsOf(published)
      .filter((entry) => entry.commentOn === undefined)
      .map((entry) => entry.item);
  const nowAsked = new Set(answersIn(schema));

  return [
    ...new Set([
      ...predecessor.module.retiredItems,
      ...answersIn(predecessor.schema).filter((item) => !nowAsked.has(item)),
    ]),
  ].sort();
};

const sorted = (values: readonly string[]): string[] => [...values].sort();

const sameSet = (
  left: readonly string[],
  right: readonly string[],
): boolean => {
  if (left.length !== right.length) {
    return false;
  }

  const ordered = sorted(right);

  return sorted(left).every((value, index) => ordered[index] === value);
};

/** What is wrong with a version's written retired list, if anything. */
export const retiredItemsIssues = (
  module: InstrumentModule,
  schema: PublishedInstrumentSchema,
  predecessor: {
    module: InstrumentModule;
    schema: PublishedInstrumentSchema;
  } | null,
): string[] => {
  const expected =
    predecessor === null ? [] : derivedRetiredItems(predecessor, schema);

  return sameSet(module.retiredItems, expected)
    ? []
    : [
        `${renderInstrumentName(module.name)} does not carry the retired keys its predecessor implies: written ${
          sorted(module.retiredItems).join(", ") || "(none)"
        }, implied ${expected.join(", ") || "(none)"}`,
      ];
};

/** Whether a version's written retired list is the one its predecessor implies. */
export const checkRetiredItems = (
  module: InstrumentModule,
  schema: PublishedInstrumentSchema,
  predecessor: {
    module: InstrumentModule;
    schema: PublishedInstrumentSchema;
  } | null,
): Result<void, InvalidError> => {
  const issues = retiredItemsIssues(module, schema, predecessor);

  return issues.length === 0
    ? ok(undefined)
    : err(
        issuesOf(
          "A version's retired keys do not follow its predecessor",
          issues,
        ),
      );
};

/** A published instrument, as the family checks see it. */
export type PublishedInstrument = {
  module: InstrumentModule;
  schema: PublishedInstrumentSchema;
};

/**
 * The two rules that hold across every version and variant of one instrument
 * family, which is the namespace item keys live in:
 *
 * - **a key is never reused.** Once a version retires a key, no version
 *   published after it may ask that key again: answers recorded under it are
 *   judgements about the question it used to name, and a later question wearing
 *   it would silently inherit them. The rule looks forward from the retirement
 *   and not backwards, because the versions that asked a key before it was
 *   retired are the reason it had to be;
 * - **a key means one thing.** Wherever a key is asked, it accepts the same
 *   values, so an aggregate over a family may read it without a mapping table.
 *   Changing what an item accepts is therefore a retire and an add, never an
 *   edit.
 *
 * Takes the members **in publication order**, which is the only thing that makes
 * "after" answerable.
 */
// TODO(revision-structure): refuse a revision whose groups, items,
// required-ness or response sets differ from the revision before it: a revision
// may change what an instrument says about what it asks — its `x-`
// annotations — never what it asks, and `SupportedVersions` serves every
// revision of a version with one set of decorators, some keyed on an item's
// response set.
export const familyIssues = (
  published: readonly PublishedInstrument[],
): string[] => {
  const issues: string[] = [];
  const byFamily = new Map<string, PublishedInstrument[]>();

  for (const entry of published) {
    const family = familyOf(entry.module.name);
    byFamily.set(family, [...(byFamily.get(family) ?? []), entry]);
  }

  for (const [family, members] of byFamily) {
    // Grows as the walk goes forward, so a version is held to what was retired
    // before it and not to what its successors went on to retire.
    const retiredBefore = new Set<string>();
    const shapes = new Map<string, { name: string; shape: string }>();

    for (const member of members) {
      const name = renderInstrumentName(member.module.name);

      for (const { item, shape } of itemsOf(member.schema)) {
        if (retiredBefore.has(item)) {
          issues.push(
            `${name} asks "${item}", which the ${family} family had already retired`,
          );
        }

        const seen = shapes.get(item);

        if (seen === undefined) {
          shapes.set(item, { name, shape });
        } else if (seen.shape !== shape) {
          issues.push(
            `"${item}" accepts different values in ${seen.name} and ${name} — a key means one thing across a family`,
          );
        }
      }

      for (const retired of member.module.retiredItems) {
        retiredBefore.add(retired);
      }
    }
  }

  return issues;
};

/** Whether a family's published members hold together. */
export const checkFamily = (
  published: readonly PublishedInstrument[],
): Result<void, InvalidError> => {
  const issues = familyIssues(published);

  return issues.length === 0
    ? ok(undefined)
    : err(issuesOf("The instrument family does not hold together", issues));
};

/**
 * Every check a manifest entry has to pass, for the whole manifest at once.
 *
 * Reports everything that is wrong rather than the first thing, because the
 * reader is somebody publishing a version and the round trip is a test run.
 */
export const checkManifest = (
  entries: readonly ManifestEntry[],
): ResultAsync<PublishedInstrument[], InvalidError> =>
  Async.fromSafePromise(collectManifestIssues(entries)).andThen(
    ({ published, issues }) =>
      issues.length === 0
        ? ok(published)
        : err(issuesOf("The instrument manifest does not hold", issues)),
  );

const collectManifestIssues = async (
  entries: readonly ManifestEntry[],
): Promise<{ published: PublishedInstrument[]; issues: string[] }> => {
  const issues: string[] = [];
  const published: PublishedInstrument[] = [];
  const byName = new Map<string, PublishedInstrument>();

  for (const entry of entries) {
    const name = renderInstrumentName(entry.module.name);
    const schema = publishedSchemaOf(entry.module);

    // A name is rendered into identifiers that are parsed back with
    // `InstrumentNameSchema`, so a module whose name it would reject could
    // publish under one nothing can read.
    if (!InstrumentNameSchema.safeParse(entry.module.name).success) {
      issues.push(`"${name}" is not a name an instrument may publish under`);
      continue;
    }

    if (schema.isErr()) {
      issues.push(`${name}: ${schema.error.message}`);
      continue;
    }

    if (byName.has(name)) {
      issues.push(`${name} is published more than once`);
      continue;
    }

    const digest = await digestOf(schema.value);

    if (digest.isErr()) {
      issues.push(`${name}: ${digest.error.message}`);
      continue;
    }

    if (entry.digest === UNPINNED) {
      issues.push(`${name} is not pinned: run \`pnpm instruments:pin\``);
    } else if (digest.value !== entry.digest) {
      issues.push(
        `${name} has changed since it was published: the manifest pins ${entry.digest}, and it now digests to ${digest.value}`,
      );
    }

    const instrument = { module: entry.module, schema: schema.value };

    issues.push(...keysMatchSchemaIssues(entry.module, schema.value));

    const predecessor =
      entry.predecessor === null ? null : byName.get(entry.predecessor);

    if (entry.predecessor !== null && predecessor === undefined) {
      issues.push(
        `${name} follows ${entry.predecessor}, which is not published before it`,
      );
    } else if (
      predecessor !== null &&
      predecessor !== undefined &&
      familyOf(predecessor.module.name) !== familyOf(entry.module.name)
    ) {
      // Lineage decides which retired keys a version is held to, and retirement
      // is a fact about one family. Following another family's version would
      // import its dead keys and leave this family's unenforced.
      issues.push(
        `${name} follows ${renderInstrumentName(predecessor.module.name)}, which is a different instrument`,
      );
    } else {
      issues.push(
        ...retiredItemsIssues(entry.module, schema.value, predecessor ?? null),
      );
    }

    byName.set(name, instrument);
    published.push(instrument);
  }

  issues.push(...familyIssues(published));

  return { published, issues };
};
